import {
  Users,
  Scale,
  Calculator,
  Headphones,
  TrendingUp,
  Server,
  UserCheck,
  ClipboardList,
  GraduationCap,
  FileSearch,
  Gavel,
  FileText,
  Receipt,
  Wallet,
  PiggyBank,
  MessageSquare,
  TicketCheck,
  Bot,
  Target,
  Mail,
  BarChart3,
  Megaphone,
  HardDrive,
  Shield,
  Workflow,
  Database,
  Zap,
  Clock,
  CheckCircle,
  Settings,
  Globe,
  Sparkles,
} from "lucide-react"
import type {
  UseCaseBasic,
  UseCaseDetail,
  UseCaseCategory,
} from "@/components/use-cases/types"

/**
 * Categories for use case filtering and navigation.
 */
export const useCaseCategories: UseCaseCategory[] = [
  {
    id: "hr-workforce",
    label: "HR & Workforce",
    icon: Users,
    description: "ARIS, recruitment, performance management",
  },
  {
    id: "legal-compliance",
    label: "Legal & Compliance",
    icon: Scale,
    description: "Qatar Labor Law, contracts, audits",
  },
  {
    id: "finance-payroll",
    label: "Finance & Payroll",
    icon: Calculator,
    description: "Gratuity, WPS, invoices",
  },
  {
    id: "customer-experience",
    label: "Customer Experience",
    icon: Headphones,
    description: "Support automation, chatbots",
  },
  {
    id: "sales-marketing",
    label: "Sales & Marketing",
    icon: TrendingUp,
    description: "Lead qualification, campaigns",
  },
  {
    id: "it-operations",
    label: "IT & Operations",
    icon: Server,
    description: "Monitoring, incident response",
  },
]

/**
 * All use cases with basic information.
 * Use cases with hasDedicatedPage: true have full detail pages.
 */
export const useCasesBasic: UseCaseBasic[] = [
  // HR & Workforce
  {
    id: "aris-hr",
    slug: "aris-hr-integration",
    title: "ARIS HR Integration",
    tagline: "Seamless integration with Oculus ARIS HR systems",
    categoryId: "hr-workforce",
    icon: UserCheck,
    tags: ["GCC", "Enterprise"],
    hasDedicatedPage: true,
  },
  {
    id: "ahr-system",
    slug: "ahr-system-automation",
    title: "AHR System Automation",
    tagline: "Automate AHR processes including leave management",
    categoryId: "hr-workforce",
    icon: ClipboardList,
    tags: ["Qatar", "Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "recruitment",
    slug: "ai-recruitment-assistant",
    title: "AI Recruitment Assistant",
    tagline: "Candidate screening, resume parsing, interview scheduling",
    categoryId: "hr-workforce",
    icon: Users,
    tags: ["All Industries"],
    hasDedicatedPage: true,
  },
  {
    id: "onboarding",
    slug: "employee-onboarding",
    title: "Employee Onboarding",
    tagline: "Automated onboarding workflows and training coordination",
    categoryId: "hr-workforce",
    icon: GraduationCap,
    tags: ["Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "performance",
    slug: "performance-management",
    title: "Performance Management",
    tagline: "Goal tracking, reviews, and 360 feedback collection",
    categoryId: "hr-workforce",
    icon: Target,
    tags: ["Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "workforce-planning",
    slug: "workforce-planning",
    title: "Workforce Planning",
    tagline: "AI-driven headcount forecasting and skill gap analysis",
    categoryId: "hr-workforce",
    icon: BarChart3,
    tags: ["Enterprise"],
    hasDedicatedPage: false,
  },

  // Legal & Compliance
  {
    id: "qatar-law-checker",
    slug: "qatar-labor-law-checker",
    title: "Qatar Labor Law Checker",
    tagline: "AI-powered compliance verification against Qatar Labor Law",
    categoryId: "legal-compliance",
    icon: Gavel,
    tags: ["Qatar", "Legal"],
    hasDedicatedPage: false,
  },
  {
    id: "gcc-regulations",
    slug: "gcc-regulatory-monitor",
    title: "GCC Regulatory Monitor",
    tagline: "Real-time monitoring of regulatory changes across GCC",
    categoryId: "legal-compliance",
    icon: FileSearch,
    tags: ["GCC", "Compliance"],
    hasDedicatedPage: false,
  },
  {
    id: "contract-analysis",
    slug: "contract-analysis",
    title: "Contract Analysis",
    tagline: "AI-driven contract review and risk identification",
    categoryId: "legal-compliance",
    icon: FileText,
    tags: ["Legal", "Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "audit-automation",
    slug: "audit-automation",
    title: "Audit Automation",
    tagline: "Automated compliance audits and documentation verification",
    categoryId: "legal-compliance",
    icon: ClipboardList,
    tags: ["Finance", "Compliance"],
    hasDedicatedPage: false,
  },
  {
    id: "policy-management",
    slug: "policy-management",
    title: "Policy Management",
    tagline: "Create, update, and distribute company policies",
    categoryId: "legal-compliance",
    icon: Shield,
    tags: ["Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "visa-immigration",
    slug: "visa-immigration",
    title: "Visa & Immigration",
    tagline: "Track visa expirations and automate renewal workflows",
    categoryId: "legal-compliance",
    icon: FileText,
    tags: ["Qatar", "GCC"],
    hasDedicatedPage: false,
  },

  // Finance & Payroll
  {
    id: "qatar-gratuity",
    slug: "qatar-gratuity-calculator",
    title: "Qatar Gratuity Calculator",
    tagline: "Automated end-of-service gratuity calculations",
    categoryId: "finance-payroll",
    icon: Calculator,
    tags: ["Qatar", "Payroll"],
    hasDedicatedPage: true,
  },
  {
    id: "wps-compliance",
    slug: "wps-compliance",
    title: "WPS Compliance",
    tagline: "Wage Protection System compliance automation",
    categoryId: "finance-payroll",
    icon: Wallet,
    tags: ["Qatar", "UAE"],
    hasDedicatedPage: false,
  },
  {
    id: "invoice-processing",
    slug: "invoice-processing",
    title: "Invoice Processing",
    tagline: "Automated invoice capture, validation, and approval",
    categoryId: "finance-payroll",
    icon: Receipt,
    tags: ["Finance", "Accounting"],
    hasDedicatedPage: false,
  },
  {
    id: "expense-management",
    slug: "expense-management",
    title: "Expense Management",
    tagline: "Employee expense submission and reimbursement processing",
    categoryId: "finance-payroll",
    icon: PiggyBank,
    tags: ["Finance", "Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "financial-reporting",
    slug: "financial-reporting",
    title: "Financial Reporting",
    tagline: "Automated report generation and variance analysis",
    categoryId: "finance-payroll",
    icon: BarChart3,
    tags: ["Finance", "Analytics"],
    hasDedicatedPage: false,
  },
  {
    id: "tax-compliance",
    slug: "tax-compliance",
    title: "VAT & Tax Compliance",
    tagline: "Automated VAT calculations and tax filing preparation",
    categoryId: "finance-payroll",
    icon: Calculator,
    tags: ["GCC", "Finance"],
    hasDedicatedPage: false,
  },

  // Customer Experience
  {
    id: "support-tickets",
    slug: "support-ticket-automation",
    title: "Support Ticket Automation",
    tagline: "24/7 ticket handling and intelligent routing",
    categoryId: "customer-experience",
    icon: TicketCheck,
    tags: ["SaaS", "Enterprise"],
    hasDedicatedPage: true,
  },
  {
    id: "claims-processing",
    slug: "claims-processing",
    title: "Claims Processing",
    tagline: "Automated insurance claims intake with fraud detection",
    categoryId: "customer-experience",
    icon: FileText,
    tags: ["Insurance", "Banking"],
    hasDedicatedPage: false,
  },
  {
    id: "chatbot-support",
    slug: "chatbot-support",
    title: "AI Chatbot Support",
    tagline: "Intelligent conversational agents in Arabic and English",
    categoryId: "customer-experience",
    icon: Bot,
    tags: ["Retail", "Banking"],
    hasDedicatedPage: false,
  },
  {
    id: "feedback-analysis",
    slug: "feedback-analysis",
    title: "Feedback Analysis",
    tagline: "Sentiment analysis and NPS tracking",
    categoryId: "customer-experience",
    icon: MessageSquare,
    tags: ["All Industries"],
    hasDedicatedPage: false,
  },
  {
    id: "escalation-management",
    slug: "escalation-management",
    title: "Smart Escalation",
    tagline: "AI-driven escalation prediction and routing",
    categoryId: "customer-experience",
    icon: Headphones,
    tags: ["Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "omnichannel-support",
    slug: "omnichannel-support",
    title: "Omnichannel Support",
    tagline: "Unified support across WhatsApp, email, and social media",
    categoryId: "customer-experience",
    icon: MessageSquare,
    tags: ["Retail", "Enterprise"],
    hasDedicatedPage: false,
  },

  // Sales & Marketing
  {
    id: "lead-qualification",
    slug: "lead-qualification",
    title: "Lead Qualification",
    tagline: "AI-powered lead scoring and automated nurturing",
    categoryId: "sales-marketing",
    icon: Target,
    tags: ["B2B", "SaaS"],
    hasDedicatedPage: true,
  },
  {
    id: "campaign-optimization",
    slug: "campaign-optimization",
    title: "Campaign Optimization",
    tagline: "A/B testing automation and budget optimization",
    categoryId: "sales-marketing",
    icon: Megaphone,
    tags: ["Marketing", "E-commerce"],
    hasDedicatedPage: false,
  },
  {
    id: "email-automation",
    slug: "email-automation",
    title: "Email Automation",
    tagline: "Personalized email campaigns and follow-up sequences",
    categoryId: "sales-marketing",
    icon: Mail,
    tags: ["Marketing", "Sales"],
    hasDedicatedPage: false,
  },
  {
    id: "sales-analytics",
    slug: "sales-analytics",
    title: "Sales Analytics",
    tagline: "Pipeline analysis and forecasting with AI insights",
    categoryId: "sales-marketing",
    icon: BarChart3,
    tags: ["Sales", "Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "content-generation",
    slug: "content-generation",
    title: "Content Generation",
    tagline: "AI-generated marketing content and product descriptions",
    categoryId: "sales-marketing",
    icon: FileText,
    tags: ["Marketing", "E-commerce"],
    hasDedicatedPage: false,
  },
  {
    id: "crm-automation",
    slug: "crm-automation",
    title: "CRM Automation",
    tagline: "Automated data entry and contact enrichment",
    categoryId: "sales-marketing",
    icon: Users,
    tags: ["Sales", "B2B"],
    hasDedicatedPage: false,
  },

  // IT & Operations
  {
    id: "infrastructure-monitoring",
    slug: "infrastructure-monitoring",
    title: "Infrastructure Monitoring",
    tagline: "24/7 system monitoring and anomaly detection",
    categoryId: "it-operations",
    icon: HardDrive,
    tags: ["Tech", "Enterprise"],
    hasDedicatedPage: false,
  },
  {
    id: "incident-resolution",
    slug: "incident-resolution",
    title: "Incident Resolution",
    tagline: "Automated incident triage and root cause analysis",
    categoryId: "it-operations",
    icon: Shield,
    tags: ["Tech", "SaaS"],
    hasDedicatedPage: false,
  },
  {
    id: "workflow-automation",
    slug: "workflow-automation",
    title: "Workflow Automation",
    tagline: "Business process automation and approval workflows",
    categoryId: "it-operations",
    icon: Workflow,
    tags: ["Enterprise", "Operations"],
    hasDedicatedPage: false,
  },
  {
    id: "data-management",
    slug: "data-management",
    title: "Data Management",
    tagline: "Data quality monitoring and ETL automation",
    categoryId: "it-operations",
    icon: Database,
    tags: ["Enterprise", "Analytics"],
    hasDedicatedPage: false,
  },
  {
    id: "resource-scaling",
    slug: "resource-scaling",
    title: "Resource Scaling",
    tagline: "Predictive resource allocation and auto-scaling",
    categoryId: "it-operations",
    icon: Server,
    tags: ["Tech", "Cloud"],
    hasDedicatedPage: false,
  },
  {
    id: "security-monitoring",
    slug: "security-monitoring",
    title: "Security Monitoring",
    tagline: "Threat detection and vulnerability scanning",
    categoryId: "it-operations",
    icon: Shield,
    tags: ["Tech", "Enterprise"],
    hasDedicatedPage: false,
  },
]

/**
 * Detailed use case data for the 5 selected use cases with dedicated pages.
 */
export const useCasesDetailed: UseCaseDetail[] = [
  // 1. ARIS HR Integration
  {
    ...useCasesBasic.find((uc) => uc.slug === "aris-hr-integration")!,
    hero: {
      badge: "HR & Workforce",
      headline: "Automate ARIS HR Operations with AI",
      subheadline:
        "Connect your Oculus ARIS system to intelligent agents that automate employee data sync, leave management, and HR workflows across your organization.",
    },
    overview: {
      challenge:
        "Managing HR operations through ARIS requires manual data entry, frequent system updates, and constant monitoring. HR teams spend hours on repetitive tasks instead of strategic initiatives.",
      solution:
        "Deploy an AI employee that integrates directly with your ARIS system. It automatically syncs employee data, processes leave requests, generates reports, and handles routine HR queries - all while maintaining compliance with GCC labor regulations.",
      impacts: [
        "70% reduction in manual data entry time",
        "Same-day processing of leave requests",
        "24/7 availability for employee HR queries",
        "Zero data sync errors between systems",
      ],
    },
    agentConfig: {
      agentName: "ARIS HR Assistant",
      description: "Automates ARIS HR operations and employee services",
      systemPromptLines: [
        "You are an HR assistant integrated with ARIS...",
        "# Guidelines",
        "- Verify employee ID before processing",
        "- Follow Qatar Labor Law for leave calculations",
        "- Escalate complex cases to HR managers",
      ],
      model: "GPT-4o",
      features: [
        { label: "Attachments", enabled: true },
        { label: "Text-to-Speech", enabled: false },
        { label: "Arabic Support", enabled: true },
        { label: "Follow-up", enabled: true },
      ],
      tools: [
        { label: "ARIS API", enabled: true },
        { label: "Email Sender", enabled: true },
        { label: "Calendar", enabled: true },
      ],
      integrations: ["ARIS", "Outlook", "Slack", "Teams"],
      triggers: [
        { label: "On employee request", source: "ARIS" },
        { label: "Daily sync", source: "Scheduler" },
      ],
      knowledgeBases: [
        { name: "HR Policies", chunks: 2500 },
        { name: "Qatar Labor Law", chunks: 1800 },
      ],
    },
    workflowConfig: {
      workflowName: "Employee Onboarding",
      status: "Active",
      nodes: [
        { id: "1", type: "trigger", label: "New Employee", sublabel: "ARIS Webhook" },
        { id: "2", type: "agent", label: "HR Assistant", sublabel: "Validate data" },
        { id: "3", type: "conditional", label: "Documents Complete?", sublabel: "Check status" },
        { id: "4", type: "action", label: "Create Accounts", sublabel: "IT Systems" },
        { id: "5", type: "output", label: "Welcome Email", sublabel: "Send to employee" },
      ],
    },
    howItWorks: [
      {
        step: 1,
        title: "Connect ARIS",
        description: "Securely connect your ARIS HR system using our enterprise-grade integration.",
        icon: Settings,
      },
      {
        step: 2,
        title: "Configure Agent",
        description: "Customize the AI agent with your HR policies and approval workflows.",
        icon: Bot,
      },
      {
        step: 3,
        title: "Deploy Workflows",
        description: "Launch automated workflows for onboarding, leave, and data sync.",
        icon: Workflow,
      },
      {
        step: 4,
        title: "Monitor & Optimize",
        description: "Track performance metrics and continuously improve with AI insights.",
        icon: BarChart3,
      },
    ],
    benefits: [
      {
        title: "70% Faster Onboarding",
        description: "Reduce employee onboarding time with automated document collection and account creation.",
        metric: "70%",
        icon: Zap,
      },
      {
        title: "24/7 HR Support",
        description: "Employees can access HR services anytime through chat or email.",
        metric: "24/7",
        icon: Clock,
      },
      {
        title: "100% Compliance",
        description: "Automatic validation against Qatar Labor Law and company policies.",
        metric: "100%",
        icon: Shield,
      },
      {
        title: "Zero Sync Errors",
        description: "Eliminate manual data entry errors with automated system synchronization.",
        metric: "0",
        icon: CheckCircle,
      },
    ],
    relatedSlugs: ["ai-recruitment-assistant", "qatar-gratuity-calculator", "employee-onboarding"],
  },

  // 2. Qatar Gratuity Calculator
  {
    ...useCasesBasic.find((uc) => uc.slug === "qatar-gratuity-calculator")!,
    hero: {
      badge: "Finance & Payroll",
      headline: "Accurate Gratuity Calculations, Every Time",
      subheadline:
        "Automate end-of-service gratuity calculations compliant with Qatar Labor Law Article 54. Handle complex scenarios with confidence.",
    },
    overview: {
      challenge:
        "Calculating end-of-service gratuity in Qatar involves complex rules based on service duration, contract type, and termination reason. Manual calculations are error-prone and time-consuming.",
      solution:
        "Deploy an AI agent that automatically calculates gratuity based on Qatar Labor Law. It handles all edge cases, generates detailed breakdowns, and integrates with your payroll system for seamless processing.",
      impacts: [
        "99.9% calculation accuracy",
        "Minutes instead of hours per calculation",
        "Full audit trail for compliance",
        "Automatic policy updates when laws change",
      ],
    },
    agentConfig: {
      agentName: "Gratuity Calculator",
      description: "Calculates end-of-service benefits per Qatar Labor Law",
      systemPromptLines: [
        "You are a financial assistant specializing in...",
        "# Qatar Labor Law Article 54",
        "- Calculate based on last basic salary",
        "- Apply service year multipliers correctly",
        "- Handle pro-rata for partial years",
      ],
      model: "GPT-4o",
      features: [
        { label: "Attachments", enabled: true },
        { label: "Calculations", enabled: true },
        { label: "Arabic Support", enabled: true },
        { label: "Export PDF", enabled: true },
      ],
      tools: [
        { label: "Calculator", enabled: true },
        { label: "PDF Generator", enabled: true },
        { label: "Email Sender", enabled: true },
      ],
      integrations: ["ARIS", "SAP", "Excel", "Email"],
      triggers: [
        { label: "On termination request", source: "HR System" },
        { label: "Manual calculation", source: "Chat" },
      ],
      knowledgeBases: [
        { name: "Qatar Labor Law", chunks: 1800 },
        { name: "Company Policies", chunks: 950 },
      ],
    },
    workflowConfig: {
      workflowName: "Gratuity Processing",
      status: "Active",
      nodes: [
        { id: "1", type: "trigger", label: "Termination", sublabel: "HR Request" },
        { id: "2", type: "agent", label: "Calculate Gratuity", sublabel: "Qatar Law" },
        { id: "3", type: "conditional", label: "Approve Amount", sublabel: "Manager review" },
        { id: "4", type: "action", label: "Process Payment", sublabel: "Payroll" },
        { id: "5", type: "output", label: "Send Statement", sublabel: "To employee" },
      ],
    },
    howItWorks: [
      {
        step: 1,
        title: "Input Employee Data",
        description: "Enter employee details or connect to your HR system for automatic data retrieval.",
        icon: Users,
      },
      {
        step: 2,
        title: "AI Calculates",
        description: "The agent applies Qatar Labor Law rules and calculates the exact gratuity amount.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Review & Approve",
        description: "Review the detailed breakdown and approve for processing.",
        icon: CheckCircle,
      },
      {
        step: 4,
        title: "Process Payment",
        description: "Automatically trigger payment through your payroll system.",
        icon: Wallet,
      },
    ],
    benefits: [
      {
        title: "99.9% Accuracy",
        description: "Eliminate calculation errors with AI-powered verification against labor law.",
        metric: "99.9%",
        icon: Target,
      },
      {
        title: "5 Min Processing",
        description: "Complete calculations in minutes instead of hours of manual work.",
        metric: "5min",
        icon: Clock,
      },
      {
        title: "Full Compliance",
        description: "Always up-to-date with the latest Qatar Labor Law requirements.",
        metric: "100%",
        icon: Shield,
      },
      {
        title: "Complete Audit Trail",
        description: "Detailed records of every calculation for compliance and audits.",
        metric: "Audit",
        icon: FileText,
      },
    ],
    relatedSlugs: ["aris-hr-integration", "wps-compliance", "financial-reporting"],
  },

  // 3. AI Recruitment Assistant
  {
    ...useCasesBasic.find((uc) => uc.slug === "ai-recruitment-assistant")!,
    hero: {
      badge: "HR & Workforce",
      headline: "Hire Smarter with AI-Powered Recruitment",
      subheadline:
        "Screen candidates, parse resumes, schedule interviews, and automate follow-ups. Find the best talent faster.",
    },
    overview: {
      challenge:
        "Recruitment teams are overwhelmed with applications, spending countless hours on resume screening, interview coordination, and candidate communication. Top candidates often slip away due to slow response times.",
      solution:
        "Deploy an AI recruitment assistant that screens resumes against job requirements, ranks candidates, schedules interviews automatically, and maintains consistent communication throughout the hiring process.",
      impacts: [
        "80% reduction in time-to-screen",
        "3x faster interview scheduling",
        "95% candidate response rate",
        "50% reduction in time-to-hire",
      ],
    },
    agentConfig: {
      agentName: "Recruitment Assistant",
      description: "Screens candidates and manages recruitment workflows",
      systemPromptLines: [
        "You are a recruitment assistant helping hire...",
        "# Screening Criteria",
        "- Match skills to job requirements",
        "- Evaluate experience relevance",
        "- Check visa/work authorization",
      ],
      model: "GPT-4o",
      features: [
        { label: "Attachments", enabled: true },
        { label: "Resume Parsing", enabled: true },
        { label: "Arabic Support", enabled: true },
        { label: "Follow-up", enabled: true },
      ],
      tools: [
        { label: "Resume Parser", enabled: true },
        { label: "Calendar", enabled: true },
        { label: "Email Sender", enabled: true },
      ],
      integrations: ["LinkedIn", "Indeed", "Outlook", "Teams"],
      triggers: [
        { label: "New application", source: "Job Portal" },
        { label: "Interview request", source: "Email" },
      ],
      knowledgeBases: [
        { name: "Job Descriptions", chunks: 500 },
        { name: "Interview Questions", chunks: 300 },
      ],
    },
    workflowConfig: {
      workflowName: "Candidate Screening",
      status: "Active",
      nodes: [
        { id: "1", type: "trigger", label: "New Application", sublabel: "Job Portal" },
        { id: "2", type: "agent", label: "Screen Resume", sublabel: "AI Analysis" },
        { id: "3", type: "conditional", label: "Qualified?", sublabel: "Score > 70%" },
        { id: "4", type: "action", label: "Schedule Interview", sublabel: "Auto-book" },
        { id: "5", type: "output", label: "Send Confirmation", sublabel: "Email candidate" },
      ],
    },
    howItWorks: [
      {
        step: 1,
        title: "Connect Job Portals",
        description: "Integrate with LinkedIn, Indeed, and your career page to capture applications.",
        icon: Globe,
      },
      {
        step: 2,
        title: "AI Screening",
        description: "Resumes are automatically parsed and scored against job requirements.",
        icon: Sparkles,
      },
      {
        step: 3,
        title: "Auto-Schedule",
        description: "Qualified candidates receive automatic interview invitations.",
        icon: Clock,
      },
      {
        step: 4,
        title: "Track Progress",
        description: "Monitor candidates through your pipeline with real-time analytics.",
        icon: BarChart3,
      },
    ],
    benefits: [
      {
        title: "80% Faster Screening",
        description: "AI screens hundreds of resumes in minutes, not days.",
        metric: "80%",
        icon: Zap,
      },
      {
        title: "3x Interview Velocity",
        description: "Automatic scheduling eliminates back-and-forth coordination.",
        metric: "3x",
        icon: Clock,
      },
      {
        title: "Better Candidate Experience",
        description: "Instant responses and clear communication at every stage.",
        metric: "95%",
        icon: MessageSquare,
      },
      {
        title: "Unbiased Screening",
        description: "AI focuses on skills and qualifications, reducing unconscious bias.",
        metric: "Fair",
        icon: Scale,
      },
    ],
    relatedSlugs: ["aris-hr-integration", "employee-onboarding", "performance-management"],
  },

  // 4. Support Ticket Automation
  {
    ...useCasesBasic.find((uc) => uc.slug === "support-ticket-automation")!,
    hero: {
      badge: "Customer Experience",
      headline: "Resolve Support Tickets 24/7 with AI",
      subheadline:
        "Automate ticket handling, intelligent routing, and resolution for common issues. Never miss a customer query again.",
    },
    overview: {
      challenge:
        "Support teams struggle with high ticket volumes, inconsistent response times, and repetitive queries. Customers expect instant responses, but human agents can't scale to meet demand.",
      solution:
        "Deploy an AI support agent that handles incoming tickets 24/7, resolves common issues instantly, intelligently routes complex cases, and learns from every interaction to improve over time.",
      impacts: [
        "60% of tickets resolved automatically",
        "90% reduction in first response time",
        "40% improvement in CSAT scores",
        "24/7 support coverage without overtime",
      ],
    },
    agentConfig: {
      agentName: "Support Agent",
      description: "Handles customer support tickets and queries",
      systemPromptLines: [
        "You are a helpful support agent for...",
        "# Resolution Guidelines",
        "- Check knowledge base first",
        "- Escalate billing issues to humans",
        "- Always confirm resolution with customer",
      ],
      model: "GPT-4o",
      features: [
        { label: "Attachments", enabled: true },
        { label: "Text-to-Speech", enabled: true },
        { label: "Arabic Support", enabled: true },
        { label: "Follow-up", enabled: true },
      ],
      tools: [
        { label: "Ticket System", enabled: true },
        { label: "Knowledge Search", enabled: true },
        { label: "Email Sender", enabled: true },
      ],
      integrations: ["Zendesk", "Freshdesk", "Slack", "Email"],
      triggers: [
        { label: "New ticket created", source: "Zendesk" },
        { label: "Customer reply", source: "Email" },
      ],
      knowledgeBases: [
        { name: "Product Docs", chunks: 5000 },
        { name: "FAQ Database", chunks: 800 },
      ],
    },
    workflowConfig: {
      workflowName: "Ticket Resolution",
      status: "Active",
      nodes: [
        { id: "1", type: "trigger", label: "New Ticket", sublabel: "Zendesk" },
        { id: "2", type: "agent", label: "Classify & Respond", sublabel: "AI Analysis" },
        { id: "3", type: "conditional", label: "Resolved?", sublabel: "Check status" },
        { id: "4", type: "action", label: "Escalate", sublabel: "To human agent" },
        { id: "5", type: "output", label: "Close Ticket", sublabel: "Send survey" },
      ],
    },
    howItWorks: [
      {
        step: 1,
        title: "Connect Helpdesk",
        description: "Integrate with Zendesk, Freshdesk, or your existing ticket system.",
        icon: Settings,
      },
      {
        step: 2,
        title: "Train Knowledge",
        description: "Upload product docs and FAQs to create the knowledge base.",
        icon: Database,
      },
      {
        step: 3,
        title: "AI Resolution",
        description: "Tickets are classified and resolved or routed automatically.",
        icon: Bot,
      },
      {
        step: 4,
        title: "Continuous Learning",
        description: "AI learns from human resolutions to handle more cases over time.",
        icon: Sparkles,
      },
    ],
    benefits: [
      {
        title: "60% Auto-Resolution",
        description: "Majority of common tickets resolved without human intervention.",
        metric: "60%",
        icon: CheckCircle,
      },
      {
        title: "90% Faster Response",
        description: "Instant acknowledgment and resolution for common issues.",
        metric: "90%",
        icon: Zap,
      },
      {
        title: "24/7 Availability",
        description: "Support customers around the clock without staffing overhead.",
        metric: "24/7",
        icon: Clock,
      },
      {
        title: "Scalable Support",
        description: "Handle 10x ticket volume without 10x the team.",
        metric: "10x",
        icon: TrendingUp,
      },
    ],
    relatedSlugs: ["chatbot-support", "feedback-analysis", "escalation-management"],
  },

  // 5. Lead Qualification
  {
    ...useCasesBasic.find((uc) => uc.slug === "lead-qualification")!,
    hero: {
      badge: "Sales & Marketing",
      headline: "Qualify Leads Automatically with AI",
      subheadline:
        "Score, qualify, and nurture leads 24/7. Your sales team focuses on closing deals, not chasing unqualified prospects.",
    },
    overview: {
      challenge:
        "Sales teams waste valuable time chasing unqualified leads while hot prospects go cold waiting for follow-up. Manual lead scoring is inconsistent and subjective.",
      solution:
        "Deploy an AI agent that scores every lead based on firmographic data and behavior, qualifies them through intelligent conversations, and automatically routes them to the right sales rep at the right time.",
      impacts: [
        "3x increase in qualified pipeline",
        "50% reduction in sales cycle time",
        "2x higher conversion rates",
        "Zero leads falling through the cracks",
      ],
    },
    agentConfig: {
      agentName: "Sales Qualifier",
      description: "Scores and qualifies inbound leads automatically",
      systemPromptLines: [
        "You are a sales development rep helping...",
        "# Qualification Criteria (BANT)",
        "- Budget: Confirm available budget",
        "- Authority: Identify decision maker",
        "- Need: Understand pain points",
      ],
      model: "GPT-4o",
      features: [
        { label: "Attachments", enabled: false },
        { label: "CRM Integration", enabled: true },
        { label: "Lead Scoring", enabled: true },
        { label: "Follow-up", enabled: true },
      ],
      tools: [
        { label: "CRM API", enabled: true },
        { label: "Email Sender", enabled: true },
        { label: "Calendar", enabled: true },
      ],
      integrations: ["Salesforce", "HubSpot", "LinkedIn", "Email"],
      triggers: [
        { label: "New lead created", source: "CRM" },
        { label: "Form submission", source: "Website" },
      ],
      knowledgeBases: [
        { name: "Product Info", chunks: 1200 },
        { name: "Sales Playbook", chunks: 450 },
      ],
    },
    workflowConfig: {
      workflowName: "Lead Qualification",
      status: "Active",
      nodes: [
        { id: "1", type: "trigger", label: "New Lead", sublabel: "CRM Webhook" },
        { id: "2", type: "agent", label: "Score & Qualify", sublabel: "BANT Analysis" },
        { id: "3", type: "conditional", label: "Qualified?", sublabel: "Score > 80" },
        { id: "4", type: "action", label: "Book Meeting", sublabel: "With sales rep" },
        { id: "5", type: "output", label: "Add to Nurture", sublabel: "Email sequence" },
      ],
    },
    howItWorks: [
      {
        step: 1,
        title: "Connect CRM",
        description: "Integrate with Salesforce, HubSpot, or your existing CRM.",
        icon: Database,
      },
      {
        step: 2,
        title: "Define Criteria",
        description: "Set up your ideal customer profile and qualification questions.",
        icon: Target,
      },
      {
        step: 3,
        title: "AI Engagement",
        description: "Leads are automatically engaged and qualified through conversation.",
        icon: MessageSquare,
      },
      {
        step: 4,
        title: "Route to Sales",
        description: "Qualified leads are routed to reps with full context and next steps.",
        icon: Users,
      },
    ],
    benefits: [
      {
        title: "3x Pipeline Growth",
        description: "More qualified leads entering your sales pipeline.",
        metric: "3x",
        icon: TrendingUp,
      },
      {
        title: "50% Faster Sales",
        description: "Shorter sales cycles with pre-qualified, engaged prospects.",
        metric: "50%",
        icon: Zap,
      },
      {
        title: "24/7 Qualification",
        description: "Never miss a lead, regardless of timezone or business hours.",
        metric: "24/7",
        icon: Clock,
      },
      {
        title: "Consistent Scoring",
        description: "Objective, data-driven lead scoring across all channels.",
        metric: "100%",
        icon: CheckCircle,
      },
    ],
    relatedSlugs: ["email-automation", "sales-analytics", "crm-automation"],
  },
]

/**
 * Helper function to get a use case by slug.
 */

/**
 * Helper function to get use cases by category.
 */

/**
 * Helper function to check if a use case has detailed data.
 */
export function hasDetailedData(slug: string): boolean {
  return useCasesDetailed.some((uc) => uc.slug === slug)
}

/**
 * Helper function to get related use cases.
 */
export function getRelatedUseCases(slugs: string[]): UseCaseBasic[] {
  return slugs
    .map((slug) => useCasesBasic.find((uc) => uc.slug === slug))
    .filter((uc): uc is UseCaseBasic => uc !== undefined)
}
